import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "../axios";
import { CRUDOperation } from "../shared/constants";
import { errorResponse, parsePostObjResponse, parseSuccessResponse } from "../shared/responseUtils";
import { deduplicateRequest } from "../shared/services/apiDeduplicationService";
import { buildApiV3Url, buildApiV3UrlWithQuery } from "../shared/Utils";
import { AppDispatch, RootState } from "./types";

// Module-level promise for deduplication of certificate jobs fetch
let certificateJobsPromise: Promise<void> | null = null;

// Types
export interface Task {
    id: number;
    taskCode: string;
    name: string;
    selected?: boolean;
}

export interface Phase {
    id: number;
    phaseCode: string;
    name: string;
    tasks: Task[];
    expanded?: boolean;
}

/** Custom field from GET jobs/{jobId}/custom-fields (display shape) */
export interface JobCustomField {
    name?: string;
    value?: string | number | null;
}

/** Raw item from GET jobs/{jobId}/custom-fields API value array */
interface JobCustomFieldRawItem {
    fieldName?: string;
    name?: string;
    value?: string | number | null;
}

/** GET jobs/{jobId}/custom-fields API (count + value wrapper) */
interface JobCustomFieldsApiResponse {
    count?: number;
    value?: JobCustomFieldRawItem[];
}

export interface Job {
    id?: number;
    jobNumber?: string;
    name?: string | null;
    exposureZoneId?: number | null;
    exposureZoneName?: string;
    phases?: Phase[];
}

// Search-related types
interface PaginateState {
    pageNumber: number;
    startIndex: number;
    endIndex: number;
    rowsPerPage: number;
    totalRecords: number;
}

interface PageState {
    data: Job[];
    paginate: PaginateState;
}

interface FormState {
    nameOrCode: string;
}

interface PaginateDetails {
    currentPage?: number;
    startIndex?: number;
    endIndex?: number;
    rowsPerPage?: number;
}

// Job Exemptions types
export interface JobExemption {
    certificateId: number | null;
    region: string;
    exemptionReason: string;
    effectiveDate: string | null;
    expirationDate: string | null;
    status: string;
}

interface JobExemptionsPaginateState {
    pageNumber: number;
    startIndex: number;
    endIndex: number;
    rowsPerPage: number;
    totalRecords: number;
}

interface JobExemptionsPageState {
    data: JobExemption[];
    paginate: JobExemptionsPaginateState;
}

interface JobsState {
    isLoading: boolean;
    job: Job;
    jobs: Job[];
    error: string | null;
    // Search functionality state
    page: PageState;
    sortColumn: [string, boolean];
    formState: FormState;
    noDataType: string;
    // Job exemptions state
    jobExemptions: JobExemptionsPageState;
    jobExemptionsLoading: boolean;
    jobExemptionsSortColumn: [string, boolean];
    // Certificate jobs state (for job details multi-select on certificate form)
    certificateJobs: Job[];
    isCertificateJobsLoading: boolean;
    // Job detail custom fields (from GET jobs/{jobId}/custom-fields)
    jobCustomFields: JobCustomField[];
    jobCustomFieldsLoading: boolean;
}

interface TaskErrors {
    taskCode: boolean;
    taskName: boolean;
}

interface PhaseErrors {
    phaseCode: boolean;
    phaseName: boolean;
    tasks: TaskErrors[];
}

interface ValidationErrorFields {
    jobNumber: boolean;
    jobName: boolean;
    region: boolean;
    phases: PhaseErrors[];
}

interface ValidationResult {
    valid: boolean;
    errorFields: ValidationErrorFields;
}

interface JobFormData {
    jobNumber: string;
    jobName: string;
    region: string;
    phases: Phase[];
}

interface JobApiData {
    jobNumber: string;
    name: string | null;
    exposureZoneId: number | null;
    phases: {
        phaseCode: string;
        name: string;
        tasks: {
            taskCode: string;
            name: string;
        }[];
    }[];
}

interface JobAsyncResult {
    success: boolean;
    job?: Job;
    error?: string;
}

interface ParsedError {
    message?: string;
}

interface SuccessResponse {
    isSuccess: boolean;
    isJobQueued?: boolean;
}

export const initialState: JobsState = {
    isLoading: false,
    job: {},
    jobs: [],
    error: null,
    // Search functionality state
    page: {
        data: [],
        paginate: {
            pageNumber: 0,
            startIndex: -1,
            endIndex: 0,
            rowsPerPage: 20,
            totalRecords: 0,
        },
    },
    sortColumn: ["id", false], // Default sort by ID descending (newest first)
    formState: {
        nameOrCode: "",
    },
    noDataType: "",
    // Job exemptions state
    jobExemptions: {
        data: [],
        paginate: {
            pageNumber: 0,
            startIndex: -1,
            endIndex: 0,
            rowsPerPage: 20,
            totalRecords: 0,
        },
    },
    jobExemptionsLoading: false,
    jobExemptionsSortColumn: ["certificateId", true],
    // Certificate jobs state
    certificateJobs: [],
    isCertificateJobsLoading: false,
    jobCustomFields: [],
    jobCustomFieldsLoading: false,
};

export const jobsSlice = createSlice({
    name: "jobs",
    initialState,
    reducers: {
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setJob: (state, action: PayloadAction<Job>) => {
            state.job = action.payload;
        },
        setJobs: (state, action: PayloadAction<Job[]>) => {
            state.jobs = action.payload;
        },
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },
        clearJob: state => {
            state.job = {};
            state.jobCustomFields = [];
        },
        clearError: state => {
            state.error = null;
        },
        // Search functionality reducers
        setFormState: (state, action: PayloadAction<FormState>) => {
            state.formState = action.payload;
        },
        setSortColumn: (state, action: PayloadAction<[string, boolean]>) => {
            state.sortColumn = action.payload;
        },
        setPage: (state, action: PayloadAction<PageState>) => {
            state.page = action.payload;
        },
        setNoDataType: (state, action: PayloadAction<string>) => {
            state.noDataType = action.payload;
        },
        // Job exemptions reducers
        setJobExemptions: (state, action: PayloadAction<JobExemptionsPageState>) => {
            state.jobExemptions = action.payload;
        },
        setJobExemptionsLoading: (state, action: PayloadAction<boolean>) => {
            state.jobExemptionsLoading = action.payload;
        },
        setJobExemptionsSortColumn: (state, action: PayloadAction<[string, boolean]>) => {
            state.jobExemptionsSortColumn = action.payload;
        },
        clearJobExemptions: state => {
            state.jobExemptions = {
                data: [],
                paginate: {
                    pageNumber: 0,
                    startIndex: -1,
                    endIndex: 0,
                    rowsPerPage: 20,
                    totalRecords: 0,
                },
            };
        },
        // Certificate jobs reducers
        setCertificateJobs: (state, action: PayloadAction<Job[]>) => {
            state.certificateJobs = action.payload;
        },
        setIsCertificateJobsLoading: (state, action: PayloadAction<boolean>) => {
            state.isCertificateJobsLoading = action.payload;
        },
        clearCertificateJobs: state => {
            state.certificateJobs = [];
        },
        setJobCustomFields: (state, action: PayloadAction<JobCustomField[]>) => {
            state.jobCustomFields = action.payload;
        },
        setJobCustomFieldsLoading: (state, action: PayloadAction<boolean>) => {
            state.jobCustomFieldsLoading = action.payload;
        },
    },
});

export const {
    setLoading,
    setJob,
    setJobs,
    setError,
    clearJob,
    clearError,
    setFormState,
    setSortColumn,
    setPage,
    setNoDataType,
    // Job exemptions actions
    setJobExemptions,
    setJobExemptionsLoading,
    setJobExemptionsSortColumn,
    clearJobExemptions,
    // Certificate jobs actions
    setCertificateJobs,
    setIsCertificateJobsLoading,
    clearCertificateJobs,
    setJobCustomFields,
    setJobCustomFieldsLoading,
} = jobsSlice.actions;

// Selectors
export const selectJob = (state: RootState): Job => state.jobs.job;
export const selectJobs = (state: RootState): Job[] => state.jobs.jobs;
export const selectIsLoading = (state: RootState): boolean => state.jobs.isLoading;
export const selectError = (state: RootState): string | null => state.jobs.error;

// Search functionality selectors
export const selectPage = (state: RootState): PageState => state.jobs.page;
export const selectLoading = (state: RootState): boolean => state.jobs.isLoading;
export const selectSortColumn = (state: RootState): [string, boolean] => state.jobs.sortColumn;
export const selectFormState = (state: RootState): FormState => state.jobs.formState;
export const selectNoData = (state: RootState): string => state.jobs.noDataType;

// Job exemptions selectors
export const selectJobExemptions = (state: RootState): JobExemptionsPageState =>
    state.jobs.jobExemptions;
export const selectJobExemptionsLoading = (state: RootState): boolean =>
    state.jobs.jobExemptionsLoading;
export const selectJobExemptionsSortColumn = (state: RootState): [string, boolean] =>
    state.jobs.jobExemptionsSortColumn;

// Certificate jobs selectors
export const selectCertificateJobs = (state: RootState): Job[] => state.jobs.certificateJobs;
export const selectIsCertificateJobsLoading = (state: RootState): boolean =>
    state.jobs.isCertificateJobsLoading;

export const selectJobCustomFields = (state: RootState): JobCustomField[] =>
    state.jobs.jobCustomFields;
export const selectJobCustomFieldsLoading = (state: RootState): boolean =>
    state.jobs.jobCustomFieldsLoading;

/**
 * Async thunk for creating a job with phases and tasks
 * @param jobData - Job data including phases and tasks
 * @returns Redux thunk
 */
export const postJobAsync =
    (jobData: JobApiData) =>
    async (dispatch: AppDispatch): Promise<JobAsyncResult> => {
        dispatch(setLoading(true));
        dispatch(setError(null));
        try {
            const response = await axios.post(buildApiV3Url("jobs"), jobData, {
                withCredentials: true,
            });

            const successResult = parseSuccessResponse(
                CRUDOperation.Create,
                response
            ) as SuccessResponse;
            if (successResult?.isSuccess) {
                const createdJob = parsePostObjResponse(response) as Job;
                dispatch(setJob(createdJob));
                return { success: true, job: createdJob };
            }
            return { success: false, error: "Create failed" };
        } catch (error: unknown) {
            const parsedError = errorResponse(error) as ParsedError | null;
            const errorMessage: string =
                parsedError?.message ?? (error instanceof Error ? error.message : "Unknown error");
            dispatch(setError(errorMessage));
            return { success: false, error: errorMessage };
        } finally {
            dispatch(setLoading(false));
        }
    };

/**
 * Async thunk for updating a job
 * @param jobId - Job ID
 * @param jobData - Updated job data
 * @returns Redux thunk
 */
export const putJobAsync =
    (jobId: number, jobData: JobApiData) =>
    async (dispatch: AppDispatch): Promise<JobAsyncResult> => {
        dispatch(setLoading(true));
        dispatch(setError(null));
        try {
            const response = await axios.put(buildApiV3Url(`jobs/${jobId}`), jobData, {
                withCredentials: true,
            });

            const successResult = parseSuccessResponse(
                CRUDOperation.Update,
                response
            ) as SuccessResponse;
            if (successResult?.isSuccess) {
                const updatedJob = parsePostObjResponse(response) as Job;
                dispatch(setJob(updatedJob));
                return { success: true, job: updatedJob };
            }
            return { success: false, error: "Update failed" };
        } catch (error: unknown) {
            const parsedError = errorResponse(error) as ParsedError | null;
            const errorMessage: string =
                parsedError?.message ?? (error instanceof Error ? error.message : "Unknown error");
            dispatch(setError(errorMessage));
            return { success: false, error: errorMessage };
        } finally {
            dispatch(setLoading(false));
        }
    };

/**
 * Async thunk for deleting a job
 * @param jobId - Job ID
 * @returns Redux thunk
 */
export const deleteJobAsync =
    (jobId: number) =>
    async (dispatch: AppDispatch): Promise<JobAsyncResult> => {
        dispatch(setLoading(true));
        dispatch(setError(null));
        try {
            const response = await axios.delete(buildApiV3Url(`jobs/${jobId}`), {
                withCredentials: true,
            });

            const successResult = parseSuccessResponse(
                CRUDOperation.Delete,
                response
            ) as SuccessResponse;
            if (successResult?.isSuccess) {
                dispatch(clearJob());
                return { success: true };
            }
            return { success: false, error: "Delete failed" };
        } catch (error: unknown) {
            const parsedError = errorResponse(error) as ParsedError | null;
            const errorMessage: string =
                parsedError?.message ?? (error instanceof Error ? error.message : "Unknown error");
            dispatch(setError(errorMessage));
            return { success: false, error: errorMessage };
        } finally {
            dispatch(setLoading(false));
        }
    };

/**
 * Async thunk for fetching a single job by ID
 * @param jobId - Job ID
 * @returns Redux thunk
 */
export const getJobByIdAsync =
    (jobId: number) =>
    async (dispatch: AppDispatch): Promise<JobAsyncResult> => {
        dispatch(setLoading(true));
        dispatch(setError(null));
        try {
            const response = await axios.get<Job>(buildApiV3Url(`jobs/${jobId}`), {
                withCredentials: true,
            });

            const job = response.data;
            dispatch(setJob(job));
            return { success: true, job };
        } catch (error: unknown) {
            const parsedError = errorResponse(error) as ParsedError | null;
            const errorMessage: string =
                parsedError?.message ?? (error instanceof Error ? error.message : "Unknown error");
            dispatch(setError(errorMessage));
            return { success: false, error: errorMessage };
        } finally {
            dispatch(setLoading(false));
        }
    };

/**
 * Fetches custom fields for a job from GET jobs/{jobId}/custom-fields.
 * Tab is shown only when the response has items.
 * @param jobId - Job ID
 * @returns Redux thunk
 */
export const getJobCustomFieldsAsync =
    (jobId: number) =>
    async (dispatch: AppDispatch): Promise<{ success: boolean; data?: JobCustomField[] }> => {
        dispatch(setJobCustomFieldsLoading(true));
        dispatch(setJobCustomFields([]));
        try {
            const response = await axios.get<JobCustomFieldsApiResponse>(
                buildApiV3Url(`jobs/${jobId}/custom-fields`),
                { withCredentials: true }
            );
            const raw: JobCustomFieldRawItem[] = Array.isArray(response.data?.value)
                ? response.data.value
                : Array.isArray(response.data)
                  ? (response.data as JobCustomFieldRawItem[])
                  : [];
            const data: JobCustomField[] = raw.map((item: JobCustomFieldRawItem) => ({
                name: item.fieldName ?? item.name,
                value: item.value,
            }));
            dispatch(setJobCustomFields(data));
            return { success: true, data };
        } catch {
            dispatch(setJobCustomFields([]));
            return { success: false };
        } finally {
            dispatch(setJobCustomFieldsLoading(false));
        }
    };

/**
 * Async thunk for deleting a phase from a job
 * @param jobId - Job ID
 * @param phaseId - Phase ID
 * @returns Redux thunk
 */
export const deletePhaseAsync =
    (jobId: number, phaseId: number) =>
    async (dispatch: AppDispatch): Promise<JobAsyncResult> => {
        dispatch(setError(null));
        try {
            const response = await axios.delete(buildApiV3Url(`jobs/${jobId}/phases/${phaseId}`), {
                withCredentials: true,
            });

            const successResult = parseSuccessResponse(
                CRUDOperation.Delete,
                response
            ) as SuccessResponse;
            if (successResult?.isSuccess) {
                return { success: true };
            }
            return { success: false, error: "Delete phase failed" };
        } catch (error: unknown) {
            const parsedError = errorResponse(error) as ParsedError | null;
            const errorMessage: string =
                parsedError?.message ?? (error instanceof Error ? error.message : "Unknown error");
            dispatch(setError(errorMessage));
            return { success: false, error: errorMessage };
        }
    };

/**
 * Async thunk for deleting a task from a phase
 * @param jobId - Job ID
 * @param phaseId - Phase ID
 * @param taskId - Task ID
 * @returns Redux thunk
 */
export const deleteTaskAsync =
    (jobId: number, phaseId: number, taskId: number) =>
    async (dispatch: AppDispatch): Promise<JobAsyncResult> => {
        dispatch(setError(null));
        try {
            const response = await axios.delete(
                buildApiV3Url(`jobs/${jobId}/phases/${phaseId}/tasks/${taskId}`),
                {
                    withCredentials: true,
                }
            );

            const successResult = parseSuccessResponse(
                CRUDOperation.Delete,
                response
            ) as SuccessResponse;
            if (successResult?.isSuccess) {
                return { success: true };
            }
            return { success: false, error: "Delete task failed" };
        } catch (error: unknown) {
            const parsedError = errorResponse(error) as ParsedError | null;
            const errorMessage: string =
                parsedError?.message ?? (error instanceof Error ? error.message : "Unknown error");
            dispatch(setError(errorMessage));
            return { success: false, error: errorMessage };
        }
    };

// Types for phase and task API data
interface PhaseApiData {
    phaseCode: string;
    name: string;
}

interface TaskApiData {
    taskCode: string;
    name: string;
}

interface PhaseWithTasksApiData extends PhaseApiData {
    tasks: TaskApiData[];
}

/**
 * Async thunk for updating job info only (without phases)
 * @param jobId - Job ID
 * @param jobData - Job info data (jobNumber, name, exposureZoneId)
 * @returns Redux thunk
 */
export const putJobInfoAsync =
    (
        jobId: number,
        jobData: { jobNumber: string; name: string | null; exposureZoneId: number | null }
    ) =>
    async (dispatch: AppDispatch): Promise<JobAsyncResult> => {
        dispatch(setError(null));
        try {
            const response = await axios.put(buildApiV3Url(`jobs/${jobId}`), jobData, {
                withCredentials: true,
            });

            const successResult = parseSuccessResponse(
                CRUDOperation.Update,
                response
            ) as SuccessResponse;
            if (successResult?.isSuccess) {
                return { success: true };
            }
            return { success: false, error: "Update job info failed" };
        } catch (error: unknown) {
            const parsedError = errorResponse(error) as ParsedError | null;
            const errorMessage: string =
                parsedError?.message ?? (error instanceof Error ? error.message : "Unknown error");
            dispatch(setError(errorMessage));
            return { success: false, error: errorMessage };
        }
    };

/**
 * Async thunk for updating an existing phase
 * @param jobId - Job ID
 * @param phaseId - Phase ID
 * @param phaseData - Phase data (phaseCode, name)
 * @returns Redux thunk
 */
export const putPhaseAsync =
    (jobId: number, phaseId: number, phaseData: PhaseApiData) =>
    async (dispatch: AppDispatch): Promise<JobAsyncResult> => {
        dispatch(setError(null));
        try {
            const response = await axios.put(
                buildApiV3Url(`jobs/${jobId}/phases/${phaseId}`),
                phaseData,
                { withCredentials: true }
            );

            const successResult = parseSuccessResponse(
                CRUDOperation.Update,
                response
            ) as SuccessResponse;
            if (successResult?.isSuccess) {
                return { success: true };
            }
            return { success: false, error: "Update phase failed" };
        } catch (error: unknown) {
            const parsedError = errorResponse(error) as ParsedError | null;
            const errorMessage: string =
                parsedError?.message ?? (error instanceof Error ? error.message : "Unknown error");
            dispatch(setError(errorMessage));
            return { success: false, error: errorMessage };
        }
    };

/**
 * Async thunk for updating an existing task
 * @param jobId - Job ID
 * @param phaseId - Phase ID
 * @param taskId - Task ID
 * @param taskData - Task data (taskCode, name)
 * @returns Redux thunk
 */
export const putTaskAsync =
    (jobId: number, phaseId: number, taskId: number, taskData: TaskApiData) =>
    async (dispatch: AppDispatch): Promise<JobAsyncResult> => {
        dispatch(setError(null));
        try {
            const response = await axios.put(
                buildApiV3Url(`jobs/${jobId}/phases/${phaseId}/tasks/${taskId}`),
                taskData,
                { withCredentials: true }
            );

            const successResult = parseSuccessResponse(
                CRUDOperation.Update,
                response
            ) as SuccessResponse;
            if (successResult?.isSuccess) {
                return { success: true };
            }
            return { success: false, error: "Update task failed" };
        } catch (error: unknown) {
            const parsedError = errorResponse(error) as ParsedError | null;
            const errorMessage: string =
                parsedError?.message ?? (error instanceof Error ? error.message : "Unknown error");
            dispatch(setError(errorMessage));
            return { success: false, error: errorMessage };
        }
    };

/**
 * Async thunk for creating a new phase
 * @param jobId - Job ID
 * @param phaseData - Phase data with tasks
 * @returns Redux thunk
 */
export const postPhaseAsync =
    (jobId: number, phaseData: PhaseWithTasksApiData) =>
    async (dispatch: AppDispatch): Promise<JobAsyncResult> => {
        dispatch(setError(null));
        try {
            const response = await axios.post(buildApiV3Url(`jobs/${jobId}/phases`), phaseData, {
                withCredentials: true,
            });

            const successResult = parseSuccessResponse(
                CRUDOperation.Create,
                response
            ) as SuccessResponse;
            if (successResult?.isSuccess) {
                return { success: true };
            }
            return { success: false, error: "Create phase failed" };
        } catch (error: unknown) {
            const parsedError = errorResponse(error) as ParsedError | null;
            const errorMessage: string =
                parsedError?.message ?? (error instanceof Error ? error.message : "Unknown error");
            dispatch(setError(errorMessage));
            return { success: false, error: errorMessage };
        }
    };

/**
 * Async thunk for creating a new task in a phase
 * @param jobId - Job ID
 * @param phaseId - Phase ID
 * @param taskData - Task data
 * @returns Redux thunk
 */
export const postTaskAsync =
    (jobId: number, phaseId: number, taskData: TaskApiData) =>
    async (dispatch: AppDispatch): Promise<JobAsyncResult> => {
        dispatch(setError(null));
        try {
            const response = await axios.post(
                buildApiV3Url(`jobs/${jobId}/phases/${phaseId}/tasks`),
                taskData,
                { withCredentials: true }
            );

            const successResult = parseSuccessResponse(
                CRUDOperation.Create,
                response
            ) as SuccessResponse;
            if (successResult?.isSuccess) {
                return { success: true };
            }
            return { success: false, error: "Create task failed" };
        } catch (error: unknown) {
            const parsedError = errorResponse(error) as ParsedError | null;
            const errorMessage: string =
                parsedError?.message ?? (error instanceof Error ? error.message : "Unknown error");
            dispatch(setError(errorMessage));
            return { success: false, error: errorMessage };
        }
    };

/**
 * Validate job form data
 * @param data - Job form data
 * @returns Validation result with valid flag and errorFields
 */
export const checkJobValidation = (data: JobFormData): ValidationResult => {
    let valid = true;
    const errorFields: ValidationErrorFields = {
        jobNumber: false,
        jobName: false,
        region: false,
        phases: [],
    };

    // Validate job number (required) - ONLY JOB CODE IS REQUIRED
    if (!data.jobNumber || data.jobNumber.trim().length === 0) {
        errorFields.jobNumber = true;
        valid = false;
    }

    // Job name and region are optional - no validation needed

    // Validate phases (if any exist, their required fields must be filled)
    if (data.phases && data.phases.length > 0) {
        data.phases.forEach((phase, phaseIndex) => {
            const phaseErrors: PhaseErrors = {
                phaseCode: false,
                phaseName: false,
                tasks: [],
            };

            // Validate phase code (required if phase exists)
            if (!phase.phaseCode || phase.phaseCode.trim().length === 0) {
                phaseErrors.phaseCode = true;
                valid = false;
            }

            // Validate phase name (required if phase exists)
            if (!phase.name || phase.name.trim().length === 0) {
                phaseErrors.phaseName = true;
                valid = false;
            }

            // Validate tasks (if any exist, their required fields must be filled)
            if (phase.tasks && phase.tasks.length > 0) {
                phase.tasks.forEach((task, taskIndex) => {
                    const taskErrors: TaskErrors = {
                        taskCode: false,
                        taskName: false,
                    };

                    // Validate task code (required if task exists)
                    if (!task.taskCode || task.taskCode.trim().length === 0) {
                        taskErrors.taskCode = true;
                        valid = false;
                    }

                    // Validate task name (required if task exists)
                    if (!task.name || task.name.trim().length === 0) {
                        taskErrors.taskName = true;
                        valid = false;
                    }

                    phaseErrors.tasks[taskIndex] = taskErrors;
                });
            }

            errorFields.phases[phaseIndex] = phaseErrors;
        });
    }

    return { valid, errorFields };
};

/**
 * Async thunk for fetching jobs with pagination, sorting, and search
 * @param pageSize - Number of rows per page
 * @param pageNumber - Current page number (1-based)
 * @param isLanding - Whether this is the initial page load
 * @param paginateDetails - Pagination details from the paginator
 * @returns Redux thunk
 */
export const fetchJobsAPI =
    (
        pageSize?: number,
        pageNumber?: number,
        isLanding?: boolean,
        paginateDetails?: PaginateDetails
    ) =>
    async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
        dispatch(setLoading(true));
        try {
            const state = getState();
            const { formState, sortColumn } = state.jobs;
            const session = state.session as { rowsPerPage: number };

            // Apply default values
            const actualPageNumber = pageNumber ?? 1;
            const actualIsLanding = isLanding ?? true;

            // Use session rowsPerPage if pageSize not provided
            const actualPageSize = pageSize ?? session.rowsPerPage;

            // Build filter string for search (using infix operator syntax)
            let filterString = "";
            if (formState.nameOrCode) {
                const searchTerm = formState.nameOrCode;
                filterString = `jobNumber contains '${searchTerm}' or name contains '${searchTerm}'`;
            }

            // Build sort string - default to id DESC for newest first
            const sortField = sortColumn[0] || "id";
            const sortDirection = sortColumn[1] ? "ASC" : "DESC";
            const sortString = `${sortField} ${sortDirection}`;

            const response = await axios.get<{
                value?: Job[];
                "@recordsetCount"?: number;
                count?: number;
            }>(
                buildApiV3UrlWithQuery(
                    "jobs",
                    filterString || null,
                    null,
                    actualPageSize,
                    (actualPageNumber - 1) * actualPageSize,
                    sortString
                ),
                { withCredentials: true }
            );

            const data: Job[] = response.data?.value || [];
            const totalRecords: number =
                response.data?.["@recordsetCount"] ?? response.data?.count ?? data.length;

            const startIndex: number =
                paginateDetails?.startIndex ?? (actualPageNumber - 1) * actualPageSize;
            const endIndex: number = paginateDetails?.endIndex ?? startIndex + actualPageSize - 1;

            dispatch(
                setPage({
                    data,
                    paginate: {
                        pageNumber: actualPageNumber,
                        startIndex,
                        endIndex,
                        rowsPerPage: actualPageSize,
                        totalRecords,
                    },
                })
            );

            if (data.length === 0) {
                dispatch(setNoDataType(actualIsLanding ? "onLanding" : "onSearch"));
            } else {
                dispatch(setNoDataType(""));
            }
        } catch {
            dispatch(setNoDataType("error"));
        } finally {
            dispatch(setLoading(false));
        }
    };

/**
 * Async thunk for fetching job exemptions details
 * @param jobId - Job ID
 * @param top - Number of rows per page
 * @param skip - Number of rows to skip
 * @param paginateDetails - Pagination details
 * @returns Redux thunk
 */
export const fetchJobExemptionsAsync =
    (jobId: number, top?: number, skip?: number, paginateDetails?: PaginateDetails) =>
    async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
        const state = getState();
        const { jobExemptionsLoading, jobExemptionsSortColumn } = state.jobs;
        const session = state.session as { rowsPerPage: number };

        if (jobExemptionsLoading) {
            return;
        }

        dispatch(setJobExemptionsLoading(true));

        try {
            const actualTop = top ?? session.rowsPerPage;
            const actualSkip = skip ?? 0;
            const sort = `${jobExemptionsSortColumn[0]} ${jobExemptionsSortColumn[1] ? "DESC" : "ASC"}`;

            const apiUrl = buildApiV3UrlWithQuery(
                `jobs/${jobId}/exemptions-details`,
                null,
                null,
                actualTop,
                actualSkip,
                sort
            );

            const response = await axios.get<{
                value?: JobExemption[];
                "@recordsetCount"?: number;
                count?: number;
            }>(apiUrl, { withCredentials: true });

            const data: JobExemption[] = response.data?.value || [];
            const totalRecords: number =
                response.data?.["@recordsetCount"] ?? response.data?.count ?? data.length;

            const startIndex: number = paginateDetails?.startIndex ?? actualSkip;
            const endIndex: number = paginateDetails?.endIndex ?? startIndex + actualTop - 1;

            dispatch(
                setJobExemptions({
                    data,
                    paginate: {
                        pageNumber: paginateDetails?.currentPage ?? 1,
                        startIndex,
                        endIndex,
                        rowsPerPage: actualTop,
                        totalRecords,
                    },
                })
            );
        } catch (error: unknown) {
            const parsedError = errorResponse(error) as ParsedError | null;
            const errorMessage: string =
                parsedError?.message ?? (error instanceof Error ? error.message : "Unknown error");
            dispatch(setError(errorMessage));
        } finally {
            dispatch(setJobExemptionsLoading(false));
        }
    };

/**
 * Unlink a certificate from a job
 * @param jobId - Job ID
 * @param certificateId - Certificate ID to unlink
 * @returns Redux thunk that resolves with success flag and optional error
 */
export const unlinkCertificateFromJobAsync =
    (jobId: number, certificateId: number) =>
    async (dispatch: AppDispatch): Promise<{ success: boolean; error?: string }> => {
        try {
            await axios.delete(buildApiV3Url(`jobs/${jobId}/certificates/${certificateId}`), {
                withCredentials: true,
            });
            dispatch(fetchJobExemptionsAsync(jobId));
            return { success: true };
        } catch (error: unknown) {
            const parsedError = errorResponse(error) as ParsedError | null;
            const errorMessage: string =
                parsedError?.message ?? (error instanceof Error ? error.message : "Unknown error");
            dispatch(setError(errorMessage));
            return { success: false, error: errorMessage };
        }
    };

/**
 * Certificate job association from API response
 */
export interface CertificateJobAssociation {
    id: number;
    certificateId: number;
    explicit: boolean;
    direct: boolean;
    jobId: number;
    jobName: string;
    jobNumber: string;
    exposureZoneName: string;
    phaseId?: number;
    phaseName?: string;
    phaseCode?: string;
    taskId?: number;
    taskName?: string;
    taskCode?: string;
}

interface CertificateJobsResponse {
    count: number;
    value: CertificateJobAssociation[];
}

/**
 * Async function for fetching job associations for an existing certificate
 * @param certificateId - Certificate ID to fetch job associations for
 * @returns Promise with array of certificate job associations, or null on error
 */
export const fetchCertificateJobsAsync = async (
    certificateId: number
): Promise<CertificateJobAssociation[] | null> => {
    try {
        const response = await axios.get<CertificateJobsResponse>(
            buildApiV3Url(`certificates/${certificateId}/jobs`),
            { withCredentials: true }
        );
        return response.data?.value || [];
    } catch {
        return null;
    }
};

/**
 * Async thunk for fetching all jobs with phases and tasks for certificate form
 * This fetches all jobs without pagination for use in the job details multi-select
 * Uses deduplicateRequest to prevent duplicate concurrent API calls
 * @returns Redux thunk
 */
export const fetchAllJobsForCertificateAsync =
    () =>
    async (dispatch: AppDispatch): Promise<void> => {
        certificateJobsPromise = deduplicateRequest(certificateJobsPromise, async () => {
            dispatch(setIsCertificateJobsLoading(true));

            try {
                // Fetch all jobs with phases and tasks included in response
                const response = await axios.get<{
                    value?: Job[];
                    "@recordsetCount"?: number;
                }>(buildApiV3UrlWithQuery("jobs", null, null, 1000, 0, "jobNumber ASC"), {
                    withCredentials: true,
                });

                const jobs: Job[] = response.data?.value || [];
                dispatch(setCertificateJobs(jobs));
            } catch (error: unknown) {
                const parsedError = errorResponse(error) as ParsedError | null;
                const errorMessage: string =
                    parsedError?.message ??
                    (error instanceof Error ? error.message : "Unknown error");
                dispatch(setError(errorMessage));
                dispatch(setCertificateJobs([]));
            } finally {
                dispatch(setIsCertificateJobsLoading(false));
                certificateJobsPromise = null; // Reset after completion
            }
        });

        return certificateJobsPromise;
    };

export default jobsSlice.reducer;
